import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export const Menu = DropdownMenu.Root
export const MenuTrigger = DropdownMenu.Trigger
export const MenuRadioGroup = DropdownMenu.RadioGroup

export const MenuContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof DropdownMenu.Content>>(
  function MenuContent({ className, sideOffset = 6, ...props }, ref) {
    return (
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          ref={ref}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(
            'glass z-50 min-w-[11rem] overflow-hidden rounded-xl p-1 text-[13.5px] shadow-popover',
            'origin-[var(--radix-dropdown-menu-content-transform-origin)] data-[state=open]:motion-safe:animate-pop-in data-[state=closed]:motion-safe:animate-pop-out',
            className,
          )}
          {...props}
        />
      </DropdownMenu.Portal>
    )
  },
)

const itemClasses =
  'relative flex cursor-default select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-ink outline-none transition-colors duration-150 ' +
  'data-[highlighted]:bg-ink/[0.06] dark:data-[highlighted]:bg-white/10 data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-muted'

export const MenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.Item> & { tone?: 'default' | 'danger' }
>(function MenuItem({ className, tone = 'default', ...props }, ref) {
  return (
    <DropdownMenu.Item
      ref={ref}
      className={cn(itemClasses, tone === 'danger' && 'text-danger [&_svg]:text-danger', className)}
      {...props}
    />
  )
})

export function MenuRadioItem({ value, children, hint }: { value: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <DropdownMenu.RadioItem value={value} className={cn(itemClasses, 'pl-8')}>
      <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex">
        <Check className="!text-accent-text" />
      </DropdownMenu.ItemIndicator>
      <span className="flex-1 truncate">{children}</span>
      {hint !== undefined && <span className="text-xs text-ink-faint">{hint}</span>}
    </DropdownMenu.RadioItem>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2 pb-1 pt-1.5 text-xs font-medium text-ink-faint">{children}</DropdownMenu.Label>
  )
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="-mx-1 my-1 h-px bg-line" />
}
